import React, { useState, useEffect, useRef } from 'react';

import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { FaEye } from 'react-icons/fa';

import { Link, useLocation } from 'react-router-dom';

import QuickViewModal from './QuickViewModal';
import StarRating from './product/StarRating';
import { useProductRatings } from '../hooks/useProductRatings';

import {
  fetchTimeRecommendations,
  readStoredUser
} from '../utils/productTracking';

/* Seconds -> a short human reading, e.g. 45s / 3m 12s / 1h 4m */
function formatDwell(total) {
  if (total < 60) {
    return `${total}s`;
  }

  const minutes = Math.floor(total / 60);

  if (minutes < 60) {
    return `${minutes}m ${total % 60}s`;
  }

  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/*
  Home-page section sitting below Top Sale. Every product the visitor has
  spent time on comes back here, longest-viewed first; the row is topped up
  with what the rest of the store dwells on if their history is thin.
*/
function SuggestedSection() {
  const [products, setProducts] = useState([]);
  const [quickViewProduct, setQuickViewProduct] = useState(null);

  /* The section is below the fold, so cards reveal on scroll rather than at
     mount — otherwise the entrance plays where nobody can see it. */
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  const location = useLocation();

  /* Parsed defensively — an unparseable userInfo here would throw during
     render and take the whole page down, not just this section. */
  const isAdmin = readStoredUser()?.isAdmin;

  const { getRating } = useProductRatings();

  useEffect(() => {
    let cancelled = false;

    fetchTimeRecommendations().then((recs) => {
      if (!cancelled) {
        setProducts(recs);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  /* Re-runs once the products land, because the node does not exist until
     then — observing before that would attach to nothing. */
  useEffect(() => {
    const node = sectionRef.current;

    if (!node || revealed) return undefined;

    if (!('IntersectionObserver' in window)) {
      setRevealed(true);

      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    io.observe(node);

    return () => io.disconnect();
  }, [products, revealed]);

  if (products.length === 0) {
    return null;
  }

  return (
    <div
      className={`suggested-section${revealed ? ' is-in' : ''}`}
      ref={sectionRef}
    >
      {/* Drifting light behind the whole section */}
      <span className="suggested-aurora" aria-hidden="true" />
      <span className="suggested-motes" aria-hidden="true">
        {Array.from({ length: 8 }).map((_, i) => (
          <span className="suggested-mote" key={i} />
        ))}
      </span>

      <Container className="position-relative">
        {/* Section heading in its own showcase card, sibling to Top Sale's —
            same construction, violet instead of ember so the two read as a
            pair rather than a repeat. */}
        <Card className="mb-5 suggested-hero">
          <span className="suggested-hero-veil" aria-hidden="true" />
          <span className="suggested-hero-weave" aria-hidden="true" />

          <Card.Body className="position-relative p-4 p-md-5">
            <Row className="align-items-center g-4">
              <Col lg={7} className="suggested-hero-copy">
                <span className="suggested-hero-eyebrow">
                  <span className="suggested-hero-dot" aria-hidden="true" />
                  Personal picks
                </span>

                <h2 className="fw-bold suggested-heading">
                  <span className="suggested-heading-text">Suggested</span>{' '}
                  <span className="suggested-heading-script">for you</span>
                </h2>

                <span className="suggested-heading-accent" />

                <p className="suggested-subtitle">
                  Ranked by the time you've spent on each one — the longer you
                  looked, the higher it sits.
                </p>

                <div className="suggested-hero-chips">
                  <span className="suggested-hero-chip">🕐 Your dwell time</span>
                  <span className="suggested-hero-chip">🪔 Divine picks</span>
                  <span className="suggested-hero-chip is-count">
                    {products.length} for you
                  </span>
                </div>

                <Button
                  as={Link}
                  to="/products"
                  className="suggested-hero-cta fw-semibold mt-4"
                >
                  Explore All Products{' '}
                  <span className="suggested-btn-arrow">→</span>
                </Button>
              </Col>

              {/* The dial states the signal the ranking is built on, so the
                  card explains itself rather than just decorating. */}
              <Col lg={5}>
                <div className="suggested-dial" aria-hidden="true">
                  <span className="suggested-dial-ring" />
                  <span className="suggested-dial-ticks" />
                  <span className="suggested-dial-orb" />

                  <div className="suggested-dial-core">
                    <span className="suggested-dial-petals" />

                    <span className="suggested-dial-om">ॐ</span>

                    <span className="suggested-dial-label">Picked for you</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Row className="g-4">
          {products.map((product, index) => {
            const oldPrice = Math.round(product.price * 1.2);
            const discount = Math.round(
              ((oldPrice - product.price) / oldPrice) * 100
            );

            /* Three across, like Top Sale — six cards fill two even rows */
            return (
              <Col
                xl={4}
                lg={4}
                md={6}
                sm={12}
                key={product._id}
                className="suggested-reveal"
                style={{ '--i': index }}
              >
                <Card className="border-0 shadow-lg h-100 suggested-card">
                  <span className="suggested-bar" aria-hidden="true" />

                  <div className="suggested-img-wrap">
                    <Card.Img
                      variant="top"
                      src={product.image}
                      alt={product.name}
                      className="suggested-image"
                    />

                    {/* Blessing light + circling sparks, offset per card so
                        the row never pulses in unison */}
                    <span
                      className="suggested-halo"
                      aria-hidden="true"
                      style={{ animationDelay: `${index * 0.6}s` }}
                    />
                    <span
                      className="suggested-orbit"
                      aria-hidden="true"
                      style={{ animationDelay: `${index * -1.4}s` }}
                    />

                    <span className="suggested-stamp" aria-hidden="true">
                      🪷
                    </span>

                    {/* Longest-viewed product earns the crown */}
                    {index === 0 ? (
                      <span className="suggested-badge is-top">
                        👑 Top Pick
                      </span>
                    ) : (
                      <span className="suggested-badge">✨ For You</span>
                    )}

                    {discount > 0 && (
                      <span className="suggested-discount">-{discount}%</span>
                    )}

                    {/* Why it ranks here. Absent on the topped-up products
                        the visitor has not opened yet. */}
                    {product.viewedSeconds > 0 && (
                      <span className="suggested-time">
                        <span className="suggested-time-ico">🕐</span>
                        {formatDwell(product.viewedSeconds)}
                      </span>
                    )}

                    <div className="suggested-overlay">
                      <button
                        type="button"
                        className="suggested-overlay-btn"
                        onClick={() => setQuickViewProduct(product)}
                      >
                        <FaEye className="me-1" /> Quick View
                      </button>
                    </div>
                  </div>

                  <Card.Body className="d-flex flex-column">
                    <Card.Title className="fw-bold mb-1 suggested-name">
                      {product.name}
                    </Card.Title>

                    <div className="mb-2 suggested-stars">
                      <StarRating
                        average={getRating(product._id).averageRating}
                        count={getRating(product._id).totalReviews}
                      />
                    </div>

                    <div className="mb-3 d-flex align-items-baseline gap-2">
                      <span className="fw-bold text-success suggested-price">
                        ₹{product.price}
                      </span>

                      <span className="text-muted text-decoration-line-through">
                        ₹{oldPrice}
                      </span>
                    </div>

                    <Button
                      as={Link}
                      to={`/product/${product._id}`}
                      variant="dark"
                      className="mt-auto w-100 suggested-btn"
                    >
                      View Details{' '}
                      <span className="suggested-btn-arrow">→</span>
                    </Button>

                    {isAdmin && (
                      <Button
                        as={Link}
                        to={`/admin/edit-product/${product._id}`}
                        state={{ from: location.pathname + location.search }}
                        variant="warning"
                        className="w-100 fw-semibold mt-2 suggested-btn"
                      >
                        ✏️ Edit Product
                      </Button>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Container>

      <QuickViewModal
        product={quickViewProduct}
        show={!!quickViewProduct}
        onHide={() => setQuickViewProduct(null)}
      />

      <style>{suggestedStyles}</style>
    </div>
  );
}

const suggestedStyles = `
  .suggested-section {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    /* Divine backdrop: soft violet + saffron glows on a warm gold base */
    background:
      radial-gradient(760px 320px at 12% -10%, rgba(124,58,237,0.14), transparent 70%),
      radial-gradient(760px 320px at 88% 110%, rgba(234,88,12,0.16), transparent 70%),
      linear-gradient(135deg, #faf5ff 0%, #fff7ed 45%, #fef3c7 100%);
    padding: clamp(40px, 8vw, 70px) 0;
  }
  /* Gold thread marking the seam with Top Sale above */
  .suggested-section::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    z-index: 2;
    background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 220% 100%;
    animation: suggestedThread 3.6s linear infinite;
  }
  @keyframes suggestedThread {
    to { background-position: 220% 0; }
  }

  .suggested-aurora {
    position: absolute;
    inset: -35%;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(32% 40% at 22% 28%, rgba(251,191,36,0.30), transparent 62%),
      radial-gradient(30% 38% at 78% 26%, rgba(168,85,247,0.26), transparent 62%),
      radial-gradient(36% 44% at 58% 84%, rgba(234,88,12,0.26), transparent 62%);
    filter: blur(40px);
    animation: suggestedAurora 24s ease-in-out infinite alternate;
    will-change: transform;
  }
  @keyframes suggestedAurora {
    0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg)  scale(1.05); }
    50%  { transform: translate3d(5%, 4%, 0)   rotate(8deg)  scale(1.18); }
    100% { transform: translate3d(-3%, 5%, 0)  rotate(-6deg) scale(1.1); }
  }

  .suggested-motes {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    overflow: hidden;
  }
  .suggested-mote {
    position: absolute;
    bottom: -14px;
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(251, 191, 36, 0.85);
    box-shadow: 0 0 12px rgba(251, 191, 36, 0.8);
    opacity: 0;
    animation: suggestedMote 14s linear infinite;
  }
  .suggested-mote:nth-child(1) { left: 8%;  animation-delay: 0s; }
  .suggested-mote:nth-child(2) { left: 21%; animation-delay: 5s;   width: 4px; height: 4px; }
  .suggested-mote:nth-child(3) { left: 35%; animation-delay: 2s; }
  .suggested-mote:nth-child(4) { left: 48%; animation-delay: 8.5s; width: 5px; height: 5px; }
  .suggested-mote:nth-child(5) { left: 62%; animation-delay: 3.4s; }
  .suggested-mote:nth-child(6) { left: 75%; animation-delay: 10.7s; width: 4px; height: 4px; }
  .suggested-mote:nth-child(7) { left: 87%; animation-delay: 6.3s; }
  .suggested-mote:nth-child(8) { left: 95%; animation-delay: 1.4s; width: 5px; height: 5px; }
  @keyframes suggestedMote {
    0%   { opacity: 0; transform: translateY(0) translateX(0) scale(0.6); }
    12%  { opacity: 0.9; }
    80%  { opacity: 0.5; }
    100% { opacity: 0; transform: translateY(-360px) translateX(26px) scale(1.1); }
  }

  /* ===== Section heading panel =====
     Deliberately unlike Top Sale's dark centred card: a light ivory panel,
     split left/right, edged with a gold-violet border that keeps travelling. */
  .suggested-hero {
    position: relative;
    overflow: hidden;
    border-radius: 30px;
    color: #1c1917;
    /* Same gold-into-violet fill as the product cards, with the moving
       gradient kept on the border layer */
    background:
      radial-gradient(120% 62% at 50% 0%, rgba(251, 191, 36, 0.42), transparent 68%) padding-box,
      linear-gradient(165deg, #fff4d9 0%, #f0dcff 44%, #ddc2fa 100%) padding-box,
      linear-gradient(115deg, #fbbf24, #f59e0b 22%, #c084fc 48%, #7c3aed 62%, #fbbf24 90%) border-box;
    background-size: 100% 100%, 100% 100%, 260% 100%;
    border: 2px solid transparent !important;
    box-shadow: 0 24px 55px rgba(124, 58, 237, 0.22) !important;
    animation: suggestedEdge 9s linear infinite;
  }
  @keyframes suggestedEdge {
    to { background-position: 0 0, 0 0, 260% 0; }
  }

  /* Colour breathing in from the corners, no blur-heavy blobs */
  .suggested-hero-veil {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(46% 70% at 3% 8%, rgba(251,191,36,0.24), transparent 70%),
      radial-gradient(44% 68% at 97% 92%, rgba(124,58,237,0.20), transparent 70%);
    animation: suggestedVeil 12s ease-in-out infinite alternate;
  }
  @keyframes suggestedVeil {
    0%   { opacity: 0.65; }
    100% { opacity: 1; }
  }
  /* Fine woven threads, fading out to the right — a temple textile, not rays */
  .suggested-hero-weave {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: repeating-linear-gradient(
      115deg,
      rgba(180, 83, 9, 0.13) 0 1px,
      transparent 1px 13px
    );
    mask-image: linear-gradient(100deg, #000 0%, transparent 62%);
    -webkit-mask-image: linear-gradient(100deg, #000 0%, transparent 62%);
  }

  .suggested-hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 3.5px;
    text-transform: uppercase;
    color: #6d28d9;
    background: rgba(124, 58, 237, 0.08);
    border: 1px solid rgba(124, 58, 237, 0.25);
    border-radius: 30px;
    padding: 6px 18px;
    margin-bottom: 18px;
  }
  /* A steady pulse rather than a twinkling star */
  .suggested-hero-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #7c3aed;
    animation: suggestedPulse 2.2s ease-in-out infinite;
  }
  @keyframes suggestedPulse {
    0%, 100% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(124,58,237,0.55); }
    50%      { transform: scale(1);   box-shadow: 0 0 0 7px rgba(124,58,237,0); }
  }

  .suggested-heading {
    display: block;
    font-size: clamp(2rem, 5.2vw, 3.1rem);
    line-height: 1.08;
    letter-spacing: -1px;
    margin-bottom: 0;
  }
  /* Ink word: deep violet with a slow gold pass through it */
  .suggested-heading-text {
    background: linear-gradient(100deg,
      #2e1065 0%, #2e1065 34%, #b45309 47%, #f59e0b 53%, #b45309 59%, #2e1065 72%, #2e1065 100%);
    background-size: 250% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: suggestedShine 6s ease-in-out infinite;
  }
  @keyframes suggestedShine {
    0%, 100% { background-position: 130% 0; }
    50%      { background-position: -30% 0; }
  }
  /* Second word set in italic gold, so the title reads as two voices */
  .suggested-heading-script {
    font-style: italic;
    font-weight: 500;
    color: #c2410c;
    letter-spacing: 0.5px;
  }
  .suggested-heading-accent {
    display: block;
    width: 96px;
    height: 3px;
    margin: 14px 0 16px;
    border-radius: 4px;
    background: linear-gradient(90deg, #f59e0b, #fbbf24, #c084fc, #f59e0b);
    background-size: 260% 100%;
    animation: suggestedAccent 4.5s linear infinite;
  }
  @keyframes suggestedAccent {
    to { background-position: 260% 0; }
  }
  .suggested-subtitle {
    max-width: 520px;
    font-size: clamp(0.95rem, 2.5vw, 1.05rem);
    color: #57534e;
  }

  .suggested-hero-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 20px;
  }
  .suggested-hero-chip {
    font-size: 0.78rem;
    font-weight: 600;
    color: #4c1d95;
    background: rgba(255, 255, 255, 0.72);
    border: 1px solid rgba(124, 58, 237, 0.22);
    border-radius: 30px;
    padding: 7px 16px;
    transition: transform 0.2s ease, border-color 0.2s ease;
  }
  .suggested-hero-chip:hover {
    transform: translateY(-2px);
    border-color: rgba(124, 58, 237, 0.5);
  }
  .suggested-hero-chip.is-count {
    color: #fde68a;
    background: linear-gradient(135deg, #4c1d95, #7c3aed);
    border-color: transparent;
    font-weight: 800;
  }

  /* Violet button, against Top Sale's gold one */
  .suggested-hero-cta {
    border: none;
    border-radius: 30px;
    padding: 12px 30px;
    color: #fef3c7;
    background: linear-gradient(135deg, #4c1d95, #7c3aed 60%, #a855f7);
    box-shadow: 0 12px 26px rgba(124, 58, 237, 0.34);
    transition: transform 0.25s ease, box-shadow 0.25s ease, filter 0.25s ease;
  }
  .suggested-hero-cta:hover,
  .suggested-hero-cta:focus {
    color: #fef3c7;
    filter: brightness(1.08);
    transform: translateY(-3px);
    box-shadow: 0 18px 34px rgba(124, 58, 237, 0.48);
  }
  .suggested-hero-cta .suggested-btn-arrow {
    display: inline-block;
    transition: transform 0.25s ease;
  }
  .suggested-hero-cta:hover .suggested-btn-arrow {
    transform: translateX(5px);
  }

  /* ===== Time dial — states the signal the ranking is built on ===== */
  .suggested-dial {
    position: relative;
    width: min(224px, 64vw);
    aspect-ratio: 1;
    margin: 0 auto;
    display: grid;
    place-items: center;
  }
  /* Ring drawn by masking a rotating conic gradient into a band */
  .suggested-dial-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: conic-gradient(from 0deg, #fbbf24, #f59e0b 25%, #a855f7 55%, #7c3aed 75%, #fbbf24);
    mask: radial-gradient(farthest-side, transparent calc(100% - 13px), #000 calc(100% - 12px));
    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 13px), #000 calc(100% - 12px));
    animation: suggestedDialSpin 16s linear infinite;
  }
  /* Minute marks just inside the ring */
  .suggested-dial-ticks {
    position: absolute;
    inset: 17px;
    border-radius: 50%;
    background: repeating-conic-gradient(from 0deg,
      rgba(124, 58, 237, 0.42) 0deg 1.2deg, transparent 1.2deg 15deg);
    mask: radial-gradient(farthest-side, transparent calc(100% - 8px), #000 calc(100% - 7px));
    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 8px), #000 calc(100% - 7px));
  }
  /* Bead travelling the ring, like time being counted */
  .suggested-dial-orb {
    position: absolute;
    inset: 0;
    animation: suggestedDialSpin 7s linear infinite;
  }
  .suggested-dial-orb::after {
    content: '';
    position: absolute;
    top: -5px; left: 50%;
    width: 13px; height: 13px;
    margin-left: -6.5px;
    border-radius: 50%;
    background: #fef3c7;
    box-shadow: 0 0 16px 5px rgba(251, 191, 36, 0.85);
  }
  @keyframes suggestedDialSpin {
    to { transform: rotate(360deg); }
  }

  .suggested-dial-core {
    position: relative;
    width: 68%;
    aspect-ratio: 1;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 6px;
    background: radial-gradient(circle at 50% 32%, #ffffff, #fff7ed 70%, #faf5ff);
    box-shadow:
      inset 0 0 0 1px rgba(251, 191, 36, 0.55),
      0 16px 32px rgba(124, 58, 237, 0.2);
  }
  /* Petal rays inside the core, turning against the ring so the two
     rotations read as separate movements rather than one solid spin */
  .suggested-dial-petals {
    position: absolute;
    inset: 7px;
    border-radius: 50%;
    background: repeating-conic-gradient(from 0deg,
      rgba(251, 191, 36, 0.22) 0deg 5deg, transparent 5deg 30deg);
    mask: radial-gradient(farthest-side, #000 55%, transparent 96%);
    -webkit-mask: radial-gradient(farthest-side, #000 55%, transparent 96%);
    animation: suggestedDialSpin 26s linear infinite reverse;
  }
  /* Om at the centre, breathing — gold into violet */
  .suggested-dial-om {
    position: relative;
    font-size: clamp(2.5rem, 6.6vw, 3.5rem);
    line-height: 1.15;
    font-weight: 700;
    background: linear-gradient(140deg, #b45309, #f59e0b 32%, #a855f7 72%, #7c3aed);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: suggestedOm 3.8s ease-in-out infinite;
  }
  @keyframes suggestedOm {
    0%, 100% {
      transform: scale(1);
      filter: drop-shadow(0 0 6px rgba(251, 191, 36, 0.55));
    }
    50% {
      transform: scale(1.09);
      filter: drop-shadow(0 0 17px rgba(168, 85, 247, 0.8));
    }
  }
  .suggested-dial-label {
    position: relative;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #a16207;
  }

  @media (max-width: 991.98px) {
    /* Stacked: the copy centres and the dial reads as a seal beneath it */
    .suggested-hero-copy { text-align: center; }
    .suggested-hero-chips { justify-content: center; }
    .suggested-heading-accent,
    .suggested-subtitle { margin-left: auto; margin-right: auto; }
    .suggested-dial { width: min(190px, 58vw); }
  }

  .suggested-card {
    border-radius: 24px;
    overflow: hidden;
    /* Kept light: a faint gold breath at the top easing into the softest
       violet, so the card reads as tinted rather than coloured in */
    background:
      radial-gradient(120% 58% at 50% 0%, rgba(251, 191, 36, 0.16), transparent 66%),
      linear-gradient(165deg, #fffefb 0%, #fdfaff 50%, #f6f1fe 100%) !important;
    border: 2px solid rgba(168, 85, 247, 0.2) !important;
    transition: transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
  }
  .suggested-card:hover {
    transform: translateY(-10px);
    border-color: rgba(124, 58, 237, 0.45) !important;
    box-shadow: 0 28px 50px rgba(124, 58, 237, 0.2) !important;
  }
  /* Violet-gold button, so the card carries one palette top to bottom */
  .suggested-btn.btn-dark {
    background: linear-gradient(135deg, #3b0764, #6d28d9 58%, #a855f7) !important;
    border: none !important;
    color: #fef3c7 !important;
  }
  .suggested-btn.btn-dark:hover,
  .suggested-btn.btn-dark:focus {
    filter: brightness(1.1);
    color: #fffbeb !important;
  }
  /* Let the card gradient read through the body */
  .suggested-card .card-body {
    background: transparent;
  }

  /* Gold-violet thread along the card's own top edge, light travelling it */
  .suggested-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    z-index: 5;
    background: linear-gradient(90deg, transparent, #c084fc 22%, #fbbf24 50%, #c084fc 78%, transparent);
    background-size: 200% 100%;
    animation: suggestedCardThread 5.5s linear infinite;
  }
  @keyframes suggestedCardThread {
    from { background-position: 200% 0; }
    to   { background-position: -200% 0; }
  }

  /* Blessing light breathing over the product */
  .suggested-halo {
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background: radial-gradient(56% 52% at 50% 42%, rgba(196, 132, 252, 0.42), transparent 72%);
    mix-blend-mode: screen;
    animation: suggestedHalo 6s ease-in-out infinite;
  }
  @keyframes suggestedHalo {
    0%, 100% { opacity: 0.32; }
    50%      { opacity: 0.9; }
  }

  /* Two sparks circling the product rather than twinkling in place */
  .suggested-orbit {
    position: absolute;
    top: 50%; left: 50%;
    width: 62%;
    aspect-ratio: 1;
    margin: -31% 0 0 -31%;
    z-index: 3;
    pointer-events: none;
    animation: suggestedOrbitSpin 11s linear infinite;
  }
  .suggested-orbit::before,
  .suggested-orbit::after {
    content: '✦';
    position: absolute;
    left: 50%;
    font-size: 0.9rem;
    line-height: 1;
    color: #fffbeb;
    text-shadow: 0 0 12px rgba(251, 191, 36, 0.95);
    animation: suggestedOrbitFade 3.6s ease-in-out infinite;
  }
  .suggested-orbit::before {
    top: -7px;
    margin-left: -6px;
  }
  .suggested-orbit::after {
    bottom: -7px;
    margin-left: -5px;
    font-size: 0.72rem;
    animation-delay: 1.8s;
  }
  @keyframes suggestedOrbitSpin {
    to { transform: rotate(360deg); }
  }
  @keyframes suggestedOrbitFade {
    0%, 100% { opacity: 0.15; }
    50%      { opacity: 1; }
  }

  /* Lotus seal pressed into the corner on hover */
  .suggested-stamp {
    position: absolute;
    bottom: 12px; right: 12px;
    z-index: 4;
    font-size: 1.15rem;
    line-height: 1;
    opacity: 0;
    transform: scale(0.4) rotate(-45deg);
    transition: opacity 0.35s ease,
      transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .suggested-card:hover .suggested-stamp {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
  /* Sparks step aside on hover so the Quick View overlay reads cleanly */
  .suggested-orbit {
    transition: opacity 0.3s ease;
  }
  .suggested-card:hover .suggested-orbit {
    opacity: 0;
  }

  /* Image set in from every edge, so the card's colour frames it instead of
     only showing beneath it */
  .suggested-img-wrap {
    position: relative;
    overflow: hidden;
    margin: 14px 14px 0;
    border-radius: 18px;
    box-shadow: 0 8px 18px rgba(76, 29, 149, 0.11);
  }
  .suggested-image {
    height: clamp(200px, 26vw, 260px);
    width: 100%;
    object-fit: cover;
    transition: transform 0.45s ease;
  }
  .suggested-card:hover .suggested-image {
    transform: scale(1.08);
  }

  .suggested-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(to top, rgba(17,24,39,0.55), rgba(17,24,39,0));
    opacity: 0;
    transition: opacity 0.35s ease;
  }
  .suggested-card:hover .suggested-overlay {
    opacity: 1;
  }
  .suggested-overlay-btn {
    background: rgba(255,255,255,0.95);
    color: #111827;
    font-weight: 700;
    font-size: 0.9rem;
    padding: 10px 20px;
    border: none;
    border-radius: 50px;
    cursor: pointer;
    transform: translateY(12px);
    transition: transform 0.35s ease, background 0.25s ease;
    box-shadow: 0 8px 20px rgba(0,0,0,0.25);
  }
  .suggested-card:hover .suggested-overlay-btn {
    transform: translateY(0);
  }
  .suggested-overlay-btn:hover {
    background: #fbbf24;
  }

  .suggested-badge {
    position: absolute;
    top: 10px; left: 10px;
    z-index: 3;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    color: #fff;
    padding: 7px 14px;
    border-radius: 30px;
    font-size: 12px;
    font-weight: bold;
    box-shadow: 0 4px 10px rgba(0,0,0,0.2);
  }
  .suggested-discount {
    position: absolute;
    top: 10px; right: 10px;
    z-index: 3;
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: #fff;
    padding: 7px 12px;
    border-radius: 30px;
    font-size: 12px;
    font-weight: bold;
    box-shadow: 0 4px 10px rgba(0,0,0,0.2);
  }

  /* The dwell reading that explains this card's position in the row */
  .suggested-time {
    position: absolute;
    bottom: 12px; left: 12px;
    z-index: 3;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 11px;
    border-radius: 30px;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.2px;
    color: #fef3c7;
    background: rgba(23, 12, 4, 0.72);
    border: 1px solid rgba(251, 191, 36, 0.45);
    backdrop-filter: blur(4px);
    transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
      border-color 0.35s ease, box-shadow 0.35s ease;
  }
  .suggested-card:hover .suggested-time {
    transform: translateY(-4px);
    border-color: rgba(251, 191, 36, 0.9);
    box-shadow: 0 6px 16px rgba(245, 158, 11, 0.4);
  }
  /* The hand ticks, so the card reads as "time spent" at a glance */
  .suggested-time-ico {
    display: inline-block;
    animation: suggestedTick 3s steps(4, end) infinite;
  }
  @keyframes suggestedTick {
    0%, 100% { transform: rotate(0deg); }
    25%      { transform: rotate(90deg); }
    50%      { transform: rotate(180deg); }
    75%      { transform: rotate(270deg); }
  }

  .suggested-name {
    font-size: 1.15rem;
  }
  .suggested-stars {
    font-size: 0.85rem;
    letter-spacing: 1px;
  }
  .suggested-price {
    font-size: 1.5rem;
  }

  .suggested-btn {
    border-radius: 12px;
    font-weight: bold;
    padding: 10px;
    transition: background 0.25s ease, transform 0.25s ease;
  }
  .suggested-btn:hover {
    transform: translateY(-2px);
  }
  .suggested-btn-arrow {
    display: inline-block;
    transition: transform 0.25s ease;
  }
  .suggested-btn:hover .suggested-btn-arrow {
    transform: translateX(5px);
  }

  /* ===== Scroll reveal =====
     Cards stay hidden until the section scrolls into view, then rise in
     sequence. Declared before the card rules so :hover still wins. */
  .suggested-reveal {
    opacity: 0;
    transform: translateY(28px) scale(0.96);
    transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1),
      transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
    transition-delay: calc(var(--i, 0) * 90ms);
  }
  .suggested-section.is-in .suggested-reveal {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  /* Gold bar unrolls along the card's bottom edge on hover */
  .suggested-bar {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 4px;
    z-index: 4;
    background: linear-gradient(90deg, #7c2d12, #ea580c 35%, #fbbf24 65%, #fde68a);
    background-size: 220% 100%;
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .suggested-card:hover .suggested-bar {
    transform: scaleX(1);
    animation: suggestedBarFlow 2.4s linear 0.45s infinite;
  }
  @keyframes suggestedBarFlow {
    to { background-position: 220% 0; }
  }

  /* The badge breathes so the personalisation is noticed */
  .suggested-badge {
    animation: suggestedBadge 2.6s ease-in-out infinite;
  }
  @keyframes suggestedBadge {
    0%, 100% { box-shadow: 0 4px 10px rgba(0,0,0,0.2); }
    50%      { box-shadow: 0 6px 18px rgba(245, 158, 11, 0.75); }
  }
  /* Top pick wears gold and a slow shimmer */
  .suggested-badge.is-top {
    background: linear-gradient(120deg, #b45309, #fbbf24 55%, #fde68a);
    background-size: 220% 100%;
    color: #431407;
    animation: suggestedBadge 2.6s ease-in-out infinite,
      suggestedTopFlow 3.6s linear infinite;
  }
  @keyframes suggestedTopFlow {
    to { background-position: 220% 0; }
  }

  .suggested-price {
    display: inline-block;
    transform-origin: left center;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .suggested-card:hover .suggested-price { transform: scale(1.09); }

  .suggested-name { transition: color 0.25s ease; }
  .suggested-card:hover .suggested-name { color: #c2410c; }

  @media (prefers-reduced-motion: reduce) {
    .suggested-section::before,
    .suggested-aurora,
    .suggested-mote,
    .suggested-hero,
    .suggested-hero-veil,
    .suggested-hero-dot,
    .suggested-dial-ring,
    .suggested-dial-orb,
    .suggested-dial-petals,
    .suggested-dial-om,
    .suggested-heading-text,
    .suggested-heading-accent,
    .suggested-badge,
    .suggested-time-ico,
    .suggested-card .suggested-img-wrap::before,
    .suggested-halo,
    .suggested-orbit,
    .suggested-orbit::before,
    .suggested-orbit::after,
    .suggested-card:hover .suggested-bar { animation: none !important; }
    .suggested-motes { display: none; }
    .suggested-orbit { display: none; }
    .suggested-halo { opacity: 0.35; }
    .suggested-stamp { transition: none; }
    /* Static fallback for the gradient-clipped word, on the ivory panel */
    .suggested-heading-text {
      -webkit-text-fill-color: #2e1065;
      color: #2e1065;
    }
    .suggested-reveal {
      opacity: 1;
      transform: none;
      transition: none;
    }
  }
`;

export default SuggestedSection;
